import React, { useState, useEffect, useMemo } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  Switch,
  Alert,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const OccupancyReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [reportPeriod, setReportPeriod] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [dateRange, setDateRange] = useState(null);
  const [isSummary, setIsSummary] = useState(false);
  const [uniqueOwnedBy, setUniqueOwnedBy] = useState([]);
  const [selectedOwnedBy, setSelectedOwnedBy] = useState(undefined);
  const [grandTotals, setGrandTotals] = useState(null);

  useEffect(() => {
    fetchOccupancyData();
  }, [financialYear, dateRange, isSummary, selectedOwnedBy]);

  const beginRequest = useLatestRequest();

  const fetchOccupancyData = async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      let url = `${apiBaseUrl}/inventoryManagement/getOccupancyReport?isSummary=${isSummary}`;

      url += `&financialYear=${financialYear}`;
      if (dateRange && dateRange.length === 2) {
        url += `&startDate=${dateRange[0].format("DD-MM-YYYY")}`;
        url += `&endDate=${dateRange[1].format("DD-MM-YYYY")}`;
      }
      if (selectedOwnedBy) url += `&ownedBy=${encodeURIComponent(selectedOwnedBy)}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      if (!isCurrent()) return;

      if (result.success) {
        setData(result.data || []);
        setReportPeriod(result.reportPeriod || null);
        if (result.uniqueOwnedBy) setUniqueOwnedBy(result.uniqueOwnedBy);
        setGrandTotals(result.grandTotals ?? null);
      } else {
        console.error("Failed to fetch occupancy data:", result.message);
        setGrandTotals(null);
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching occupancy data:", error);
      setGrandTotals(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  // Set filtered data to match data
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      let url = `${apiBaseUrl}/inventoryManagement/getOccupancyReport?isSummary=${isSummary}&isExcel=true`;

      url += `&financialYear=${financialYear}`;
      if (dateRange && dateRange.length === 2) {
        url += `&startDate=${dateRange[0].format("DD-MM-YYYY")}`;
        url += `&endDate=${dateRange[1].format("DD-MM-YYYY")}`;
      }
      if (selectedOwnedBy) url += `&ownedBy=${encodeURIComponent(selectedOwnedBy)}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        const fileName = isSummary
          ? `Occupancy_Summary_Report_${financialYear}${dateRange?.length === 2 ? `_${dateRange[0].format("DD-MM-YYYY")}_to_${dateRange[1].format("DD-MM-YYYY")}` : ""}.xlsx`
          : `Occupancy_Detailed_Report_${financialYear}${dateRange?.length === 2 ? `_${dateRange[0].format("DD-MM-YYYY")}_to_${dateRange[1].format("DD-MM-YYYY")}` : ""}.xlsx`;

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  /** Purchase / monthly breakdown may include paise */
  const formatCurrencyDecimals = (amount) => {
    if (amount == null || amount === "") return "N/A";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(2) || 0}%`;
  };

  const currencyCellStyle = { whiteSpace: "nowrap", fontSize: "12px" };

  const monthIncomeKeys = useMemo(() => {
    const row = data.find(
      (r) => r?.monthlyIncome && typeof r.monthlyIncome === "object",
    );
    if (!row) return [];
    return Object.keys(row.monthlyIncome);
  }, [data]);

  // Booked days can be a half day: a full site's booking is shared by its two halves.
  const formatDays = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Number.isInteger(n) ? n : n.toFixed(1);
  };

  const detailedColumns = useMemo(() => {
    const monthDaysCols = monthIncomeKeys.map((mk) => ({
      title: `${mk} Days`,
      key: `monthlyDaysBooked-${mk}`,
      width: 100,
      align: "center",
      render: (_, record) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {formatDays(record?.monthlyDaysBooked?.[mk] ?? 0)}
        </span>
      ),
    }));

    const monthCols = monthIncomeKeys.map((mk) => ({
      title: mk,
      key: `monthlyIncome-${mk}`,
      width: 118,
      align: "center",
      render: (_, record) => {
        const v = record?.monthlyIncome?.[mk];
        return v != null && v !== "" ? formatCurrencyDecimals(v) : "—";
      },
    }));

    return [
      {
        title: "Code No.",
        dataIndex: "codeNo",
        key: "codeNo",
        width: 120,
        align: "center",
        render: (code) => (
          <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
            {code || "N/A"}
          </span>
        ),
      },
      {
        title: "Area",
        dataIndex: "area",
        key: "area",
        width: 160,
        align: "center",
        render: (area) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{area || "N/A"}</span>
        ),
      },
      {
        title: "Zone",
        dataIndex: "zone",
        key: "zone",
        width: 100,
        align: "center",
        render: (zone) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{zone || "N/A"}</span>
        ),
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        width: 300,
        render: (location) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{location || "N/A"}</span>
        ),
      },
      {
        title: "Media",
        dataIndex: "media",
        key: "media",
        width: 100,
        align: "center",
        render: (media) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{media || "N/A"}</span>
        ),
      },
      {
        title: "Illu",
        dataIndex: "illu",
        key: "illu",
        width: 72,
        align: "center",
        render: (illu) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{illu || "N/A"}</span>
        ),
      },
      {
        title: "Size (sq ft)",
        dataIndex: "sizeSqFt",
        key: "sizeSqFt",
        width: 100,
        align: "center",
        render: (size) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{size ?? "N/A"}</span>
        ),
      },
      {
        title: "Owned By",
        dataIndex: "ownedBy",
        key: "ownedBy",
        width: 120,
        align: "center",
        render: (ownedBy) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{ownedBy || "N/A"}</span>
        ),
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        width: 110,
        align: "center",
        render: (d) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{d || "N/A"}</span>
        ),
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        width: 110,
        align: "center",
        render: (d) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{d || "N/A"}</span>
        ),
      },
      {
        title: "Purchase / Month",
        dataIndex: "purchasePerMonth",
        key: "purchasePerMonth",
        width: 168,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>
            {v != null && v !== "" ? formatCurrencyDecimals(v) : "N/A"}
          </span>
        ),
      },
      ...monthCols,
      {
        title: "Total Days",
        dataIndex: "totalDays",
        key: "totalDays",
        width: 100,
        align: "center",
        render: (days) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{days ?? 0}</span>
        ),
      },
      {
        title: "Days Booked",
        dataIndex: "daysBooked",
        key: "daysBooked",
        width: 110,
        align: "center",
        render: (days) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{formatDays(days ?? 0)}</span>
        ),
      },
      ...monthDaysCols,
      {
        title: "Occupied %",
        dataIndex: "occupiedPct",
        key: "occupiedPct",
        width: 100,
        align: "center",
        render: (percentage) => (
          <span
            style={{
              color:
                percentage > 50
                  ? "#52c41a"
                  : percentage > 25
                    ? "#fa8c16"
                    : "#ff4d4f",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {formatPercentage(percentage)}
          </span>
        ),
      },
      {
        title: "Non-Occupied %",
        dataIndex: "nonOccupiedPct",
        key: "nonOccupiedPct",
        width: 152,
        align: "center",
        render: (percentage) => (
          <span style={{ color: "#333", fontSize: "12px" }}>
            {percentage != null && percentage !== "" ? formatPercentage(percentage) : "N/A"}
          </span>
        ),
      },
      {
        title: "Income",
        dataIndex: "income",
        key: "income",
        width: 155,
        align: "center",
        render: (income) => (
          <span style={{ color: "#333", ...currencyCellStyle }}>
            {income != null && income !== "" ? formatCurrency(income) : "N/A"}
          </span>
        ),
      },
      {
        title: "Expense",
        dataIndex: "expense",
        key: "expense",
        width: 155,
        align: "center",
        render: (expense) => (
          <span style={{ color: "#333", ...currencyCellStyle }}>
            {expense != null && expense !== "" ? formatCurrency(expense) : "N/A"}
          </span>
        ),
      },
      {
        title: "Profit/Loss",
        dataIndex: "profitLoss",
        key: "profitLoss",
        width: 155,
        align: "center",
        render: (profitLoss) => (
          <span
            style={{
              color: profitLoss >= 0 ? "#52c41a" : "#ff4d4f",
              fontWeight: "bold",
              ...currencyCellStyle,
            }}
          >
            {profitLoss != null && profitLoss !== "" ? formatCurrency(profitLoss) : "N/A"}
          </span>
        ),
      },
      {
        title: "P/L %",
        dataIndex: "plPct",
        key: "plPct",
        width: 100,
        align: "center",
        render: (percentage) => (
          <span
            style={{
              color: percentage >= 0 ? "#52c41a" : "#ff4d4f",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {percentage != null && percentage !== "" ? formatPercentage(percentage) : "N/A"}
          </span>
        ),
      },
      {
        title: "Occupancy Months",
        dataIndex: "occupancyMonths",
        key: "occupancyMonths",
        width: 168,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v ?? "N/A"}</span>
        ),
      },
      {
        title: "Month Category",
        dataIndex: "monthCategory",
        key: "monthCategory",
        width: 150,
        align: "center",
        render: (v) => (
          <span style={{ color: "#333", fontSize: "12px" }}>{v || "N/A"}</span>
        ),
      },
    ];
  }, [monthIncomeKeys]);

  /** Columns from Code No. through Non-Occupied % (before Income): month income + month booked days */
  const detailedColsBeforeIncome = 15 + monthIncomeKeys.length * 2;

  const getColumnsForSummaryView = () => [
    {
      title: "Month Range",
      dataIndex: "monthWise",
      key: "monthWise",
      width: 200,
      align: "center",
      render: (monthWise) => (
        <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
          {monthWise || "N/A"}
        </span>
      ),
    },
    {
      title: "Number of Sites",
      dataIndex: "noOfSites",
      key: "noOfSites",
      width: 150,
      align: "center",
      render: (sites) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{sites ?? 0}</span>
      ),
    },
    {
      title: "Income",
      dataIndex: "income",
      key: "income",
      width: 150,
      align: "center",
      render: (income) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {income != null && income !== "" ? formatCurrency(income) : "N/A"}
        </span>
      ),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "14px",
            fontWeight: "bold",
          }}
        />
      ),
    },
  };

  const archivedOccupancyRowProps = (record) => {
    if (isSummary || record?.isArchived !== true) return {};
    return { className: "occupancy-archived-row" };
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <style>{`
        .occupancy-archived-row > td,
        .occupancy-archived-row > td span {
          color: #ff4d4f !important;
        }
        .occupancy-report-table .ant-table-summary > tr > td {
          white-space: nowrap;
          background-color: #fafafa;
        }
        .occupancy-report-table .ant-table-summary > tr > td strong {
          white-space: nowrap;
        }
      `}</style>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Occupancy Report
            </Space>
            <Space>
              <Switch
                checkedChildren="Summary"
                unCheckedChildren="Detailed"
                checked={isSummary}
                onChange={setIsSummary}
              />
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="DD-MM-YYYY"
                placeholder={["Start Date", "End Date"]}
              />
              <Select
                placeholder="Filter by Group"
                value={selectedOwnedBy}
                onChange={setSelectedOwnedBy}
                style={{ width: 160 }}
                allowClear
                showSearch
                filterOption={(input, opt) =>
                  (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={(uniqueOwnedBy || []).map((g) => ({ label: g, value: g }))}
              />
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                style={{ marginLeft: "8px" }}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {reportPeriod && (
          <Alert
            message={`Report Period: ${reportPeriod.startDate} to ${reportPeriod.endDate}${
              reportPeriod.financialYear ? ` (FY: ${reportPeriod.financialYear})` : ""
            }${reportPeriod.totalDaysInPeriod != null ? ` • ${reportPeriod.totalDaysInPeriod} days` : ""}`}
            type="info"
            style={{ marginBottom: "16px" }}
          />
        )}

        <Table
          className="occupancy-report-table"
          columns={isSummary ? getColumnsForSummaryView() : detailedColumns}
          dataSource={filteredData}
          loading={loading}
          components={components}
          onRow={archivedOccupancyRowProps}
          rowKey={(record, index) =>
            isSummary
              ? record.monthWise || `summary-${index}`
              : record.codeNo + (record.location || "") + index
          }
          sticky
          pagination={false}
          scroll={
            isSummary
              ? undefined
              : {
                  y: 800,
                  x: Math.max(2800, 500 + detailedColumns.length * 110),
                }
          }
          summary={() =>
            grandTotals ? (
              isSummary ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Space wrap>
                        <strong>Grand totals</strong>
                        <span>
                          Income: {formatCurrency(grandTotals.income ?? 0)}
                        </span>
                        <span>
                          Expense: {formatCurrency(grandTotals.expense ?? 0)}
                        </span>
                        <span>
                          Profit / Loss:{" "}
                          {formatCurrency(grandTotals.profitLoss ?? 0)}
                        </span>
                      </Space>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={detailedColsBeforeIncome}
                      align="center"
                    >
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={detailedColsBeforeIncome} align="center">
                      <strong style={currencyCellStyle}>
                        {formatCurrency(grandTotals.income ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={detailedColsBeforeIncome + 1} align="center">
                      <strong style={currencyCellStyle}>
                        {formatCurrency(grandTotals.expense ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={detailedColsBeforeIncome + 2} align="center">
                      <strong style={currencyCellStyle}>
                        {formatCurrency(grandTotals.profitLoss ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={detailedColsBeforeIncome + 3} align="center" />
                    <Table.Summary.Cell index={detailedColsBeforeIncome + 4} align="center" />
                    <Table.Summary.Cell index={detailedColsBeforeIncome + 5} align="center" />
                  </Table.Summary.Row>
                </Table.Summary>
              )
            ) : null
          }
        />
      </Card>
    </div>
  );
};

export default OccupancyReport;
